/**
 * Navigation surfaces for the monograph demo shell.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DEMO_CATALOG,
  demoLocalUrl,
  isWorkspaceEntry,
  type DemoCatalogWorkspaceEntry,
} from "../../catalog/catalog-data.js";
import { useKeyboardListNav } from "../../hooks/index.js";
import type { MonographDemoNavEntry, MonographDemoTheme } from "./types.js";

export { MonographTopBar } from "./top-bar.js";

export const DEFAULT_NARROW_VIEWPORT_QUERY = "(max-width: 900px)";
export const DEFAULT_SIDEBAR_WIDTH = 290;

// The app-switcher in MonographTopBar is a per-port concept — it
// teleports the browser from one localhost:<port> to another. Hub-internal
// demos have no per-port URL, so they are filtered out here; users reach
// those through the hub launcher (`npm run demo:hub`).
const WORKSPACE_ENTRIES: readonly DemoCatalogWorkspaceEntry[] =
  DEMO_CATALOG.filter(isWorkspaceEntry);
const CATALOG_BY_SCRIPT = new Map(WORKSPACE_ENTRIES.map((entry) => [entry.script, entry]));
const HUB_ENTRY = WORKSPACE_ENTRIES[0];
const HUB_HREF = HUB_ENTRY === undefined ? "/" : demoLocalUrl(HUB_ENTRY);

export function adjacentMonographPanelId<PanelId extends string>(
  ids: readonly PanelId[],
  currentId: PanelId,
  delta: -1 | 1,
): PanelId {
  const fallback = ids[0];
  if (fallback === undefined) {
    throw new Error("MonographDemoShell requires at least one nav entry");
  }
  const currentIndex = ids.indexOf(currentId);
  const safeIndex = currentIndex >= 0 ? currentIndex : 0;
  const nextIndex = (safeIndex + delta + ids.length) % ids.length;
  return ids[nextIndex] ?? fallback;
}

interface NavItemProps<PanelId extends string> {
  readonly entry: MonographDemoNavEntry<PanelId>;
  readonly isActive: boolean;
  readonly onSelect: (id: PanelId) => void;
  readonly theme: MonographDemoTheme;
  readonly registerRef: (id: PanelId, node: HTMLButtonElement | null) => void;
}

function NavItem<PanelId extends string>({
  entry,
  isActive,
  onSelect,
  theme,
  registerRef,
}: NavItemProps<PanelId>): React.JSX.Element {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      ref={(node) => {
        registerRef(entry.id, node);
      }}
      type="button"
      aria-current={isActive ? "page" : undefined}
      tabIndex={isActive ? 0 : -1}
      onClick={() => {
        onSelect(entry.id);
      }}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      className="relative mb-0.5 flex w-full cursor-pointer items-start gap-3 overflow-hidden rounded-lg border-0 px-3 py-2.5 text-left transition"
      style={{
        background: isActive
          ? `linear-gradient(135deg, ${entry.accent}24, rgba(248,250,252,0.055))`
          : hovered
            ? "rgba(248,250,252,0.07)"
            : "transparent",
        boxShadow: isActive ? `inset 0 0 0 1px ${entry.accent}40` : "none",
      }}
    >
      {isActive ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            left: 0,
            top: "18%",
            bottom: "18%",
            width: 3,
            borderRadius: "0 2px 2px 0",
            background: entry.accent,
            boxShadow: `0 0 10px ${entry.accent}`,
          }}
        />
      ) : null}

      <span
        aria-hidden
        className="shrink-0 font-mono text-[17px] leading-[1.3] transition-colors"
        style={{
          color: isActive ? entry.accent : theme.muted,
          textShadow: isActive ? `0 0 12px ${entry.accent}` : "none",
        }}
      >
        {entry.icon}
      </span>

      <span className="flex min-w-0 flex-col">
        <span
          className="text-[13.5px] leading-[1.3] transition-colors"
          style={{
            fontWeight: isActive ? 700 : 500,
            color: isActive ? theme.bright : theme.text,
          }}
        >
          {entry.label}
        </span>
        <span
          className="mt-0.5 font-mono text-[11px]"
          style={{
            color: theme.muted,
            letterSpacing: "0.02em",
          }}
        >
          {entry.subtitle}
        </span>
      </span>
    </button>
  );
}

export interface MonographSidebarProps<PanelId extends string> {
  readonly activeId: PanelId;
  readonly nav: readonly MonographDemoNavEntry<PanelId>[];
  readonly navIds: readonly PanelId[];
  readonly onSelect: (id: PanelId) => void;
  readonly drawerOpen: boolean;
  readonly isOffCanvas: boolean;
  readonly onCloseDrawer: () => void;
  readonly theme: MonographDemoTheme;
  readonly title: string;
  readonly eyebrow: string;
  readonly tagline: string;
  readonly companionPath: string;
  readonly currentScript?: string | undefined;
  readonly sidebarWidth: number;
}

export function MonographSidebar<PanelId extends string>({
  activeId,
  nav,
  navIds,
  onSelect,
  drawerOpen,
  isOffCanvas,
  onCloseDrawer,
  theme,
  title,
  eyebrow,
  tagline,
  companionPath,
  currentScript,
  sidebarWidth,
}: MonographSidebarProps<PanelId>): React.JSX.Element {
  const itemRefs = useRef<Map<PanelId, HTMLButtonElement>>(new Map());
  const focusOnArrowRef = useRef(false);

  const registerRef = useCallback((id: PanelId, node: HTMLButtonElement | null): void => {
    if (node === null) itemRefs.current.delete(id);
    else itemRefs.current.set(id, node);
  }, []);

  const onSelectFromKey = useCallback(
    (id: PanelId): void => {
      focusOnArrowRef.current = true;
      onSelect(id);
    },
    [onSelect],
  );

  useEffect(() => {
    if (!focusOnArrowRef.current) return;
    focusOnArrowRef.current = false;
    itemRefs.current.get(activeId)?.focus();
  }, [activeId]);

  const { onKeyDown } = useKeyboardListNav<PanelId>({
    items: navIds,
    activeId,
    onSelect: onSelectFromKey,
    orientation: "vertical",
  });

  const handleSelect = useCallback(
    (id: PanelId): void => {
      onSelect(id);
      if (isOffCanvas) onCloseDrawer();
    },
    [isOffCanvas, onCloseDrawer, onSelect],
  );
  const currentCatalogScript = useMemo(() => {
    if (currentScript === undefined) return "";
    return CATALOG_BY_SCRIPT.has(currentScript) ? currentScript : "";
  }, [currentScript]);
  const appSwitcherId = `${title.replace(/\W+/g, "-").toLowerCase()}-app-switcher`;
  const handleAppChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>): void => {
      const entry = CATALOG_BY_SCRIPT.get(event.target.value);
      if (entry === undefined || entry.script === currentCatalogScript) {
        return;
      }
      window.location.assign(demoLocalUrl(entry));
    },
    [currentCatalogScript],
  );

  const isHiddenOffCanvas = isOffCanvas && !drawerOpen;
  const aside = (
    <aside
      aria-label={`${title} sections`}
      aria-hidden={isHiddenOffCanvas || undefined}
      inert={isHiddenOffCanvas}
      className="flex h-screen shrink-0 flex-col overflow-y-auto border-r backdrop-blur-xl"
      style={{
        width: sidebarWidth,
        position: isOffCanvas ? "fixed" : "sticky",
        top: 0,
        left: 0,
        background: [
          "linear-gradient(135deg, rgba(20,184,166,0.10) 0%, transparent 42%)",
          "linear-gradient(225deg, rgba(168,85,247,0.08) 0%, transparent 38%)",
          `linear-gradient(180deg, color-mix(in srgb, ${theme.surface} 92%, #f8fafc 8%) 0%, color-mix(in srgb, ${theme.void} 90%, #111827 10%) 100%)`,
        ].join(", "),
        borderRight: `1px solid ${theme.border}`,
        zIndex: 50,
        transform: isOffCanvas
          ? drawerOpen
            ? "translateX(0)"
            : `translateX(-${sidebarWidth}px)`
          : undefined,
        transition: isOffCanvas ? "transform 0.2s ease" : undefined,
        boxShadow: isOffCanvas && drawerOpen ? "0 16px 32px rgba(0,0,0,0.5)" : undefined,
        pointerEvents: isHiddenOffCanvas ? "none" : undefined,
      }}
    >
      <div
        className="border-b px-5 pb-5 pt-6"
        style={{
          borderBottom: `1px solid ${theme.border}`,
        }}
      >
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden
            className="inline-flex h-7 w-7 items-center justify-center rounded-lg border font-mono text-sm"
            style={{
              border: `1px solid ${theme.border}`,
              background: `color-mix(in srgb, ${theme.accent} 14%, transparent)`,
              color: theme.accent,
              textShadow: `0 0 10px ${theme.accent}99`,
            }}
          >
            ⊕
          </span>
          <div
            className="text-[15px] font-extrabold leading-tight"
            style={{
              color: theme.bright,
            }}
          >
            {title}
          </div>
        </div>
        <div
          className="mt-2 font-mono text-[11px]"
          style={{
            color: theme.muted,
            letterSpacing: "0.05em",
          }}
        >
          {eyebrow}
        </div>
        <div
          className="mt-1.5 text-[11.5px] italic leading-normal"
          style={{
            color: theme.muted,
          }}
        >
          {tagline}
        </div>
        <div
          aria-hidden
          style={{
            marginTop: 12,
            height: 2,
            borderRadius: 1,
            background: `linear-gradient(90deg, ${nav.map((entry) => entry.accent).join(", ")})`,
            opacity: 0.7,
          }}
        />
      </div>

      <nav aria-label={`${title} navigation`} onKeyDown={onKeyDown} className="flex-1 px-3 py-3">
        {nav.map((entry) => (
          <NavItem
            key={entry.id}
            entry={entry}
            isActive={entry.id === activeId}
            onSelect={handleSelect}
            theme={theme}
            registerRef={registerRef}
          />
        ))}
      </nav>

      <div
        className="grid gap-2 border-t px-3.5 py-3"
        style={{
          borderTop: `1px solid ${theme.border}`,
        }}
      >
        <label
          htmlFor={appSwitcherId}
          className="font-mono text-[10px] uppercase"
          style={{
            color: theme.muted,
            letterSpacing: "0.06em",
          }}
        >
          Apps
        </label>
        <select
          id={appSwitcherId}
          aria-label="Open another CSK demo app"
          value={currentCatalogScript}
          onChange={handleAppChange}
          className="min-h-8 w-full cursor-pointer appearance-none rounded-lg border px-2.5 pr-7 text-xs outline-none"
          style={{
            background: "rgba(248,250,252,0.07)",
            border: `1px solid ${theme.border}`,
            color: theme.bright,
            fontFamily: theme.sans,
          }}
        >
          {currentCatalogScript === "" ? <option value="">Open another demo</option> : null}
          {WORKSPACE_ENTRIES.map((entry) => (
            <option key={entry.script} value={entry.script}>
              {entry.title}
            </option>
          ))}
        </select>
        <div
          className="flex min-w-0 items-center justify-between gap-2 font-mono text-[10.5px]"
          style={{
            color: theme.muted,
          }}
        >
          <a href={HUB_HREF} style={{ color: theme.text, textDecoration: "none" }}>
            Hub
          </a>
          <span
            aria-label={`Companion manuscript path: ${companionPath}`}
            title={companionPath}
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Manuscript
          </span>
        </div>
      </div>
    </aside>
  );

  if (!isOffCanvas) return aside;

  return (
    <>
      {drawerOpen ? (
        <button
          type="button"
          aria-label="Close navigation"
          onClick={onCloseDrawer}
          className="fixed inset-0 z-40 cursor-pointer border-0 bg-black/50 backdrop-blur-sm"
        />
      ) : null}
      {aside}
    </>
  );
}
